import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@components/Button';
import { InvoiceEmitError, InvoiceEmitErrorField } from '../types/invoice';

// ─── Field labels (HTTP 400 validation) ───────────────────────────────────────

export const INVOICE_FIELD_LABELS: Record<string, string> = {
  ship_addr_street:      'Logradouro',
  ship_addr_num:         'Número',
  ship_addr_district:    'Bairro',
  ship_addr_city:        'Cidade',
  ship_addr_state:       'UF',
  ship_addr_zip:         'CEP',
  customer_name:         'Nome do cliente',
  customer_document:     'CPF/CNPJ',
  customer_tipo_pessoa:  'Tipo de pessoa',
  customer_contribuinte: 'Contribuinte',
  'items[0].code':       'Código do item',
};

// ─── ValidationErrors Modal (HTTP 400) ───────────────────────────────────────

export const InvoiceValidationErrorsModal: React.FC<{
  details: Record<string, string>;
  message?: string;
  onClose: () => void;
}> = ({ details, message, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
      <div className="flex items-center justify-between px-6 py-4 border-b border-amber-200 bg-amber-50 rounded-t-xl">
        <div className="flex items-center gap-2">
          <AlertTriangle size={20} className="text-amber-600" />
          <h2 className="text-lg font-bold text-amber-900">Campos Obrigatórios</h2>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-amber-100 rounded-full transition-colors">
          <X size={18} className="text-amber-600" />
        </button>
      </div>
      <div className="px-6 py-4 space-y-2">
        <p className="text-sm text-slate-600 mb-3">
          {message || 'Corrija os seguintes campos antes de emitir a nota fiscal:'}
        </p>
        <ul className="space-y-1.5">
          {Object.entries(details).map(([field, msg]) => (
            <li key={field} className="flex items-start gap-2 text-sm">
              <AlertTriangle size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <span>
                <span className="font-medium text-slate-800">{INVOICE_FIELD_LABELS[field] ?? field}:</span>
                {' '}<span className="text-slate-600">{msg}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="px-6 py-3 border-t border-slate-200 flex justify-end">
        <Button variant="outline" onClick={onClose}>Fechar</Button>
      </div>
    </div>
  </div>
);

// ─── Error Modal Component (HTTP 200 success=false) ───────────────────────────

const renderFields = (fields: InvoiceEmitErrorField[], depth = 0) => (
  <ul className={`space-y-2 ${depth > 0 ? 'ml-4 border-l-2 border-red-200 pl-3' : ''}`}>
    {fields.map((f, i) => (
      <li key={i} className="text-sm">
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-medium text-red-800">{f.element}</span>
            {f.code ? <span className="text-red-400 ml-1">(código {f.code})</span> : null}
            <p className="text-red-700">{f.msg}</p>
            {f.collection && f.collection.length > 0 && renderFields(f.collection, depth + 1)}
          </div>
        </div>
      </li>
    ))}
  </ul>
);

/**
 * Resolve the modal title dynamically based on error type and content.
 *
 * VALIDATION_ERROR:
 *   - May indicate NCM ausente, parcelas inválidas, or any other server-side validation.
 *   - Uses `error.message` directly so the user sees the actual error, not a hardcoded string.
 * EMISSION_BLOCKED:
 *   - Bloqueio temporário do guardião de rejeições repetidas (não é um erro de sistema).
 *   - Usa `error.message` (que já traz o motivo/tempo) e é exibido como aviso, não erro.
 * DEST_FISCAL_DIVERGENCE:
 *   - Contribuinte ICMS/IE da tela diverge do resolvido (consulta cadastro SEFAZ / cadastro
 *     do cliente). Aviso, não erro: a tela já se auto-corrigiu com os valores de `fields`
 *     e o operador só confere e reemite.
 * BLING_ERROR / SEFAZ_ERROR:
 *   - Shows a generic "Erro na Emissão…" / "Erro na Alteração…" header.
 * INTERNAL_ERROR / unknown:
 *   - Shows "Erro Interno".
 */
const resolveTitle = (error: InvoiceEmitError, contextLabel: string) => {
  switch (error.type) {
    case 'VALIDATION_ERROR':
      return error.message || 'Erro de Validação';
    case 'EMISSION_BLOCKED':
      return error.message || 'Emissão Bloqueada Temporariamente';
    case 'DEST_FISCAL_DIVERGENCE':
      return error.message || 'Divergência Fiscal do Destinatário';
    case 'CARRIER_AMOUNT_LIMIT':
      return error.message || 'Valor da nota acima do limite da transportadora';
    case 'BLING_ERROR':
    case 'SEFAZ_ERROR':
      return `Erro ${contextLabel} da Nota Fiscal`;
    default:
      return 'Erro Interno';
  }
};

export const InvoiceEmitErrorModal: React.FC<{
  error: InvoiceEmitError;
  /** Label contextual — "na Emissão" ou "na Alteração" */
  contextLabel?: string;
  onClose: () => void;
}> = ({ error, contextLabel = 'na Emissão', onClose }) => {
  // EMISSION_BLOCKED é um bloqueio temporário esperado, DEST_FISCAL_DIVERGENCE é uma
  // correção pré-emissão (a tela já se auto-ajustou) e CARRIER_AMOUNT_LIMIT é uma regra
  // de negócio com saída conhecida (desmembrar o pedido): todos usam o estilo de aviso
  // (âmbar) do VALIDATION_ERROR em vez do vermelho, que sugeriria falha do sistema.
  const isWarning =
    error.type === 'VALIDATION_ERROR' ||
    error.type === 'EMISSION_BLOCKED' ||
    error.type === 'DEST_FISCAL_DIVERGENCE' ||
    error.type === 'CARRIER_AMOUNT_LIMIT';
  const borderColor = isWarning ? 'border-amber-200' : 'border-red-200';
  const bgColor = isWarning ? 'bg-amber-50' : 'bg-red-50';
  const iconColor = isWarning ? 'text-amber-600' : 'text-red-600';
  const titleColor = isWarning ? 'text-amber-900' : 'text-red-900';
  const typeColor = isWarning ? 'text-amber-600' : 'text-red-600';
  const msgColor = isWarning ? 'text-amber-700' : 'text-red-700';

  const title = resolveTitle(error, contextLabel);

  // Renderizado via portal com z-index acima do Dialog (que também usa portal, z-50). Sem
  // isso, quando o erro ocorre dentro de um Dialog aberto (ex.: modal de devolução), o
  // Dialog fica por cima e a modal de erro não aparece.
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col mx-4">
        <div className={`flex items-center justify-between px-6 py-4 border-b ${borderColor} ${bgColor} rounded-t-xl`}>
          <div className="flex items-center gap-2">
            <AlertTriangle size={20} className={iconColor} />
            <h2 className={`text-lg font-bold ${titleColor}`}>{title}</h2>
          </div>
          <button onClick={onClose} className={`p-1.5 hover:bg-slate-100 rounded-full transition-colors`}>
            <X size={18} className={iconColor} />
          </button>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          <div>
            {!isWarning && (
              <p className="text-sm font-semibold text-slate-700">Tipo: <span className={`font-normal ${typeColor}`}>{error.type}</span></p>
            )}
            <p className={`text-sm font-semibold text-slate-700 ${!isWarning ? 'mt-1' : ''}`}>
              Mensagem: <span className={`font-normal ${msgColor}`}>{error.message}</span>
            </p>
            {error.description && (
              <p className={`text-sm text-slate-600 mt-2 p-3 rounded-lg border ${isWarning ? 'bg-amber-50 border-amber-100' : 'bg-red-50 border-red-100'}`}>{error.description}</p>
            )}
          </div>
          {/* Show fields for ALL error types, not just non-warning — exceto
              CARRIER_AMOUNT_LIMIT, cujos fields são dados legíveis por máquina
              (provider_name/max_amount/invoice_total/order_id) e não erros de campo:
              a descrição já diz tudo, e listá-los aqui só confundiria. */}
          {error.type !== 'CARRIER_AMOUNT_LIMIT' && error.fields && error.fields.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Detalhes dos erros por campo:</h3>
              {renderFields(error.fields)}
            </div>
          )}
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex justify-end">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
